import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { ImageIcon, Calendar, Tag, ExternalLink, ArrowLeft, Leaf, HeartHandshake, HeartPulse, GraduationCap, X, ChevronLeft, ChevronRight, FolderOpen, Images } from "lucide-react";

const getApiBase = () => {
  if (typeof window !== "undefined" && window.location && window.location.origin) {
    return `${window.location.origin}/api`;
  }
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return base ? `${base}/api` : "/api";
};

const API = getApiBase();

const TAG_STYLES = {
  environment: { bg: "bg-[#5c7c2e]", text: "text-white", Icon: Leaf, label: "Environment" },
  donation: { bg: "bg-[#c47750]", text: "text-white", Icon: HeartHandshake, label: "Donation" },
  "medical aid": { bg: "bg-[#a63d55]", text: "text-white", Icon: HeartPulse, label: "Medical Aid" },
  "educational aid": { bg: "bg-[#2f6b78]", text: "text-white", Icon: GraduationCap, label: "Educational Aid" },
  general: { bg: "bg-[#5a4a3a]", text: "text-white", Icon: Tag, label: "General" },
};

const styleForTag = (t) => TAG_STYLES[String(t || "").toLowerCase()] || { bg: "bg-[#5a4a3a]", text: "text-white", Icon: Tag, label: t || "General" };

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch (e) { return String(iso); }
};

// ---------- Google Drive helpers ----------
const extractDriveId = (url) => {
  if (!url || typeof url !== "string") return null;
  let m = url.match(/\/file\/d\/([A-Za-z0-9_-]{10,})/);
  if (m) return m[1];
  m = url.match(/[?&]id=([A-Za-z0-9_-]{10,})/);
  if (m) return m[1];
  m = url.match(/\/d\/([A-Za-z0-9_-]{10,})/);
  if (m) return m[1];
  return null;
};

const isFolderUrl = (url) => /drive\.google\.com\/(drive\/)?folders?\//.test(url || "");

const directImageUrl = (url, w = 1600) => {
  const id = extractDriveId(url);
  if (id) return `https://drive.google.com/thumbnail?id=${id}&sz=w${w}`;
  return url;
};

// Merge image_urls[] and photo_link (possibly comma-separated) into one clean list
const parsePhotoUrls = (activity) => {
  const out = [];
  const push = (u) => {
    if (typeof u !== "string") return;
    u.split(",").forEach((p) => {
      const v = p.trim();
      if (v && !out.includes(v)) out.push(v);
    });
  };
  (activity.image_urls || []).forEach(push);
  push(activity.photo_link);
  return out;
};

// ---------- Fullscreen Lightbox Gallery ----------
const PhotoLightbox = ({ activity, images, index, onClose, onIndexChange }) => {
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const total = images.length;
  const current = images[index];
  const touchStartX = useRef(null);

  const prev = useCallback(() => onIndexChange((index - 1 + total) % total), [index, total, onIndexChange]);
  const next = useCallback(() => onIndexChange((index + 1) % total), [index, total, onIndexChange]);

  useEffect(() => { setLoaded(false); setLoadError(false); }, [index]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, prev, next]);

  useEffect(() => {
    [(index + 1) % total, (index - 1 + total) % total].forEach((i) => {
      if (i !== index && total > 1) { const img = new Image(); img.src = directImageUrl(images[i]); }
    });
  }, [index, images, total]);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40 && total > 1) { dx > 0 ? prev() : next(); }
    touchStartX.current = null;
  };

  return (
    <div
      data-testid="photo-lightbox"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex flex-col bg-[#1d140f]/95 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Header bar */}
      <div className="flex items-center justify-between gap-3 px-4 md:px-6 py-3 text-[#f6efd8]" onClick={(e) => e.stopPropagation()}>
        <div className="min-w-0">
          <h3 className="font-bold text-base md:text-lg truncate" style={{ fontFamily: "'Quicksand', system-ui, sans-serif" }}>
            {activity.event_title}
          </h3>
          <p className="text-xs opacity-75">
            {formatDate(activity.activity_date_time)} · Photo {index + 1} of {total}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <a
            data-testid="lightbox-drive-link"
            href={current}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5 text-xs font-semibold transition-colors"
          >
            <FolderOpen size={14} />
            <span className="hidden sm:inline">Open in Drive</span>
          </a>
          <button
            data-testid="lightbox-close-button"
            onClick={onClose}
            aria-label="Close gallery"
            className="rounded-full bg-white/10 hover:bg-white/20 p-2 transition-colors"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Main image */}
      <div
        className="relative flex-1 flex items-center justify-center px-3 md:px-16 min-h-0"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {!loaded && !loadError && <div className="absolute text-[#f6efd8]/60 text-sm">Loading photo…</div>}
        {loadError && (
          <div data-testid="lightbox-error" className="text-center text-[#f6efd8] px-6">
            <ImageIcon size={40} className="mx-auto mb-3 opacity-60" />
            <p className="text-sm font-semibold mb-1">This photo could not be displayed</p>
            <p className="text-xs opacity-75 max-w-sm mx-auto mb-3">
              The Google Drive file may not be shared publicly. Set it to "Anyone with the link can view".
            </p>
            <a
              href={current}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 px-4 py-2 text-xs font-semibold transition-colors"
            >
              <FolderOpen size={14} /> Try opening in Google Drive
            </a>
          </div>
        )}
        <img
          key={current}
          data-testid="lightbox-image"
          src={directImageUrl(current)}
          alt={`${activity.event_title} — photo ${index + 1}`}
          onLoad={() => setLoaded(true)}
          onError={() => { setLoaded(true); setLoadError(true); }}
          className={`max-h-full max-w-full object-contain rounded-lg shadow-2xl transition-opacity duration-300 ${loaded && !loadError ? "opacity-100" : "opacity-0 absolute"}`}
        />
        {total > 1 && (
          <>
            <button
              data-testid="lightbox-prev-button"
              onClick={prev}
              aria-label="Previous photo"
              className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/25 text-[#f6efd8] p-2.5 transition-colors"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              data-testid="lightbox-next-button"
              onClick={next}
              aria-label="Next photo"
              className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/25 text-[#f6efd8] p-2.5 transition-colors"
            >
              <ChevronRight size={22} />
            </button>
          </>
        )}
      </div>

      {/* Thumbnail strip */}
      {total > 1 && (
        <div data-testid="lightbox-thumbnails" className="flex gap-2 justify-center overflow-x-auto px-4 py-3" onClick={(e) => e.stopPropagation()}>
          {images.map((p, i) => (
            <button
              key={i}
              data-testid={`lightbox-thumb-${i}`}
              onClick={() => onIndexChange(i)}
              aria-label={`Photo ${i + 1}`}
              className={`shrink-0 w-16 h-12 rounded-md overflow-hidden ring-2 transition-all ${i === index ? "ring-[#e9b872]" : "ring-transparent opacity-60 hover:opacity-100"}`}
            >
              <img src={directImageUrl(p, 300)} alt={`Thumbnail ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- Activity card ----------
const ActivityCard = ({ activity, onOpen }) => {
  const primaryTag = activity.tags && activity.tags[0];
  const tagMeta = styleForTag(primaryTag);
  const { Icon } = tagMeta;
  const [coverFailed, setCoverFailed] = useState(false);

  const all = parsePhotoUrls(activity);
  const folderLink = all.find(isFolderUrl) || null;
  const images = all.filter((u) => !isFolderUrl(u));
  const hasMedia = images.length > 0 || Boolean(folderLink);
  const cover = !coverFailed && images[0] ? directImageUrl(images[0], 900) : null;

  const open = () => {
    if (images.length > 0) onOpen(activity, 0);
    else if (folderLink) window.open(folderLink, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      data-testid={`activity-card-${activity.id}`}
      onClick={open}
      role={hasMedia ? "button" : undefined}
      tabIndex={hasMedia ? 0 : -1}
      onKeyDown={(e) => (hasMedia && (e.key === "Enter" || e.key === " ") ? open() : null)}
      className={`group relative overflow-hidden rounded-2xl bg-white ring-1 ring-[#e5d7bd] shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${hasMedia ? "cursor-pointer" : ""}`}
    >
      {/* Tag stripe */}
      <div className={`h-1.5 w-full ${tagMeta.bg}`} />

      {/* Cover photo with count badge */}
      {cover && (
        <div className="relative h-40 overflow-hidden bg-[#efe6d6]">
          <img src={cover} alt={activity.event_title} loading="lazy" onError={() => setCoverFailed(true)} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          <div data-testid={`photo-count-${activity.id}`} className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/60 text-white px-2.5 py-1 text-[11px] font-semibold">
            <Images size={12} />
            {images.length} {images.length === 1 ? "photo" : "photos"}
          </div>
        </div>
      )}

      <div className="p-5 flex flex-col gap-3 min-h-[140px]">
        {/* Icon + tag chip */}
        <div className="flex items-center justify-between">
          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${tagMeta.bg} ${tagMeta.text}`}>
            <Icon size={13} />
            {tagMeta.label}
          </div>
          {hasMedia && !cover && (
            <span className="inline-flex items-center gap-1 text-[#8a7560] text-xs font-medium opacity-80 group-hover:opacity-100">
              {folderLink ? <FolderOpen size={15} /> : <ExternalLink size={15} />}
              {folderLink ? "Drive folder" : `${images.length} ${images.length === 1 ? "photo" : "photos"}`}
            </span>
          )}
        </div>

        {/* Title */}
        <h3 className="text-[#3a2c22] font-bold text-lg leading-snug line-clamp-2"
            style={{ fontFamily: "'Quicksand', system-ui, sans-serif" }}>
          {activity.event_title}
        </h3>

        {/* Date */}
        <div className="mt-auto flex items-center gap-1.5 text-xs text-[#7a6553]">
          <Calendar size={13} />
          {formatDate(activity.activity_date_time)}
        </div>
      </div>

      {/* Hover overlay with description */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#3a2c22]/95 to-[#5c3a31]/95 text-[#f6efd8] opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col p-5">
        <div className="flex items-center gap-1.5 mb-3">
          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${tagMeta.bg} ${tagMeta.text}`}>
            <Icon size={13} />
            {tagMeta.label}
          </div>
        </div>
        <h4 className="font-bold text-base mb-2 line-clamp-2" style={{ fontFamily: "'Quicksand', system-ui, sans-serif" }}>
          {activity.event_title}
        </h4>
        <p className="text-sm leading-relaxed line-clamp-5 opacity-90">
          {activity.event_description}
        </p>
        {hasMedia && (
          <div className="mt-auto pt-3 text-xs inline-flex items-center gap-1.5 opacity-90">
            {images.length > 0 ? (
              <>
                <Images size={14} /> Click to view {images.length} {images.length === 1 ? "photo" : "photos"}
              </>
            ) : (
              <>
                <FolderOpen size={14} /> Click to open Drive folder
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ActivitiesPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewer, setViewer] = useState(null); // { activity, index }

  useEffect(() => {
    document.title = "Activities — Sharada Rama Rao Trust";
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/activities`);
        if (!cancelled) setItems(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        if (!cancelled) setError("Could not load activities. Please try again in a moment.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const openViewer = (activity, index) => setViewer({ activity, index });

  return (
    <div className="min-h-screen bg-[#dad7c9]">
      {/* Header */}
      <div className="bg-[#4a2c25] text-[#f6efd8] py-8 md:py-12">
        <div className="max-w-6xl mx-auto px-6">
          <a href="/" data-testid="back-to-home-link" className="inline-flex items-center gap-1.5 text-sm opacity-80 hover:opacity-100 mb-4">
            <ArrowLeft size={14} /> Back to home
          </a>
          <div className="flex items-center gap-4">
            <img src="/sr-logo.png" alt="SR Trust" className="w-16 h-16 md:w-20 md:h-20 rounded-md bg-[#f6efd8] p-1" />
            <div>
              <h1 className="font-bold text-3xl md:text-4xl leading-tight"
                  style={{ fontFamily: "'Quicksand', system-ui, sans-serif" }}>
                Trust Activities
              </h1>
              <p className="mt-1 text-sm md:text-base opacity-85">
                A record of the work carried out by Sharada Rama Rao Trust — click any card to view photos.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-6xl mx-auto px-6 py-10 md:py-14">
        {loading && (
          <div data-testid="activities-loading" className="text-center text-[#5a4a3a] py-16">Loading activities…</div>
        )}
        {!loading && error && (
          <div data-testid="activities-error" className="text-center text-[#7a2e2e] py-16">{error}</div>
        )}
        {!loading && !error && items.length === 0 && (
          <div data-testid="activities-empty" className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#efe6d6] text-[#8a7560] mb-4">
              <ImageIcon size={28} />
            </div>
            <p className="text-[#5a4a3a] text-base max-w-lg mx-auto">
              No activities have been recorded yet. New activities will appear here automatically as they are added.
            </p>
          </div>
        )}
        {!loading && !error && items.length > 0 && (
          <div data-testid="activities-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {items.map((a) => (
              <ActivityCard key={a.id} activity={a} onOpen={openViewer} />
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {viewer && (
        <PhotoLightbox
          activity={viewer.activity}
          images={parsePhotoUrls(viewer.activity).filter((u) => !isFolderUrl(u))}
          index={viewer.index}
          onClose={() => setViewer(null)}
          onIndexChange={(i) => setViewer((v) => ({ ...v, index: i }))}
        />
      )}
    </div>
  );
};

export default ActivitiesPage;
